import { applyObjectDefaultParameters, uuid } from '@@common';

import { ComponentMetadataModel, ParameterMetadata } from '../../../api/api';
import { ITEM_TYPE_SHARED_COMPONENT, ITEM_TYPE_VIEW, SelectedItem, SharedComponentSelectionContext } from '../Components/AppArtifactPanel';
import { Bundle } from '../Models/Bundle';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { SharedComponentModel } from '../Models/SharedComponentModel';

export type ComponentPath = string[];

/** Filters out the Children parameter, which is managed by the tree editor rather than the properties panel. */
export function filterTreeManagedParameters(parameters: ParameterMetadata[]): ParameterMetadata[] {
	return parameters.filter((p) => p.name !== ('Children' satisfies keyof IContainerComponentModel));
}

/** Immutably get a component node at the given path within the children tree */
export function getComponentAtPath(children: IComponentModel[], path: ComponentPath): IComponentModel | null {
	if (path.length === 0) return null;
	if (!Array.isArray(children)) return null;
	const [head, ...rest] = path;
	const node = children.find((c) => c.Id === head);
	if (!node) return null;
	if (rest.length === 0) return node;
	const container = node as IContainerComponentModel;
	if (!Array.isArray(container.Children)) return null;
	return getComponentAtPath(container.Children, rest);
}

/** Immutably update a component at the given path */
export function updateComponentAtPath<T extends IContainerComponentModel>(view: T, path: ComponentPath, updated: IComponentModel): T {
	if (path.length === 0) return view;
	const updater = (children: IComponentModel[], p: ComponentPath): IComponentModel[] => {
		const [head, ...rest] = p;
		return children.map((child) => {
			if (child.Id !== head) return child;
			if (rest.length === 0) return updated;
			const container = child as IContainerComponentModel;
			return {
				...container,
				Children: updater(container.Children ?? [], rest),
			};
		});
	};
	return { ...view, Children: updater(view.Children, path) } as T;
}

/** Immutably add a child at the container path */
export function addComponentAtPath<T extends IContainerComponentModel>(view: T, path: ComponentPath, newChild: IComponentModel): T {
	const adder = (children: IComponentModel[], p: ComponentPath): IComponentModel[] => {
		if (p.length === 0) {
			return [...children, newChild];
		}
		const [head, ...rest] = p;
		return children.map((child) => {
			if (child.Id !== head) return child;
			const container = child as IContainerComponentModel;
			if (rest.length === 0) {
				return {
					...container,
					Children: [...(container.Children ?? []), newChild],
				};
			}
			return {
				...container,
				Children: adder(container.Children ?? [], rest),
			};
		});
	};
	return { ...view, Children: adder(view.Children, path) } as T;
}

export function createDefaultComponent(metadata: ComponentMetadataModel): IComponentModel {
	const base: IComponentModel = { Id: uuid(), Type: metadata.name, Layout: {} };

	// Apply default parameter values from metadata
	const withDefaults = applyObjectDefaultParameters(base, metadata.parameters);

	if (metadata.isContainer) {
		return { ...withDefaults, Children: [] } as IContainerComponentModel;
	}
	return withDefaults;
}

/** Get parent children array and current component index for a given path */
export function getParentInfo<T extends IContainerComponentModel>(
	view: T,
	path: ComponentPath,
): { parentChildren: IComponentModel[]; currentIndex: number } | null {
	if (path.length === 0) return null;

	// Navigate to parent
	const parentPath = path.slice(0, -1);
	const targetId = path[path.length - 1];

	let parentChildren: IComponentModel[];
	if (parentPath.length === 0) {
		// Root level
		parentChildren = view.Children;
	} else {
		// Nested component
		const parentComponent = getComponentAtPath(view.Children, parentPath) as IContainerComponentModel;
		if (!parentComponent || !Array.isArray(parentComponent.Children)) return null;
		parentChildren = parentComponent.Children;
	}

	const currentIndex = parentChildren.findIndex((c) => c.Id === targetId);
	if (currentIndex === -1) return null;

	return { parentChildren, currentIndex };
}

/** Move component up (towards index 0) in its parent's children array */
export function moveComponentUp<T extends IContainerComponentModel>(view: T, path: ComponentPath): { updatedView: T; newPath: ComponentPath } | null {
	const parentInfo = getParentInfo(view, path);
	if (!parentInfo || parentInfo.currentIndex === 0) return null; // Already first

	const { parentChildren, currentIndex } = parentInfo;
	const newChildren = [...parentChildren];
	[newChildren[currentIndex - 1], newChildren[currentIndex]] = [newChildren[currentIndex], newChildren[currentIndex - 1]];

	const parentPath = path.slice(0, -1);
	const updatedView = updateChildrenAtPath(view, parentPath, newChildren);
	const newPath = [...parentPath, newChildren[currentIndex - 1].Id];

	return { updatedView, newPath };
}

/** Move component down (towards end of array) in its parent's children array */
export function moveComponentDown<T extends IContainerComponentModel>(view: T, path: ComponentPath): { updatedView: T; newPath: ComponentPath } | null {
	const parentInfo = getParentInfo(view, path);
	if (!parentInfo || parentInfo.currentIndex === parentInfo.parentChildren.length - 1) return null; // Already last

	const { parentChildren, currentIndex } = parentInfo;
	const newChildren = [...parentChildren];
	[newChildren[currentIndex], newChildren[currentIndex + 1]] = [newChildren[currentIndex + 1], newChildren[currentIndex]];

	const parentPath = path.slice(0, -1);
	const updatedView = updateChildrenAtPath(view, parentPath, newChildren);
	const newPath = [...parentPath, newChildren[currentIndex + 1].Id];

	return { updatedView, newPath };
}

/** Helper: Update children array at a specific path */
function updateChildrenAtPath<T extends IContainerComponentModel>(view: T, path: ComponentPath, newChildren: IComponentModel[]): T {
	if (path.length === 0) {
		// Root level
		return { ...view, Children: newChildren } as T;
	}

	const updater = (children: IComponentModel[], p: ComponentPath): IComponentModel[] => {
		const [head, ...rest] = p;
		return children.map((child) => {
			if (child.Id !== head) return child;
			const container = child as IContainerComponentModel;
			if (rest.length === 0) {
				return { ...container, Children: newChildren };
			}
			return { ...container, Children: updater(container.Children ?? [], rest) };
		});
	};

	return { ...view, Children: updater(view.Children, path) } as T;
}

/** Remove component at the specified path */
export function removeComponentAtPath<T extends IContainerComponentModel>(view: T, path: ComponentPath): T | null {
	if (path.length === 0) return null; // Cannot remove root

	const parentPath = path.slice(0, -1);
	const targetId = path[path.length - 1];

	let parentChildren: IComponentModel[];
	if (parentPath.length === 0) {
		// Root level
		parentChildren = view.Children;
	} else {
		// Nested component
		const parentComponent = getComponentAtPath(view.Children, parentPath) as IContainerComponentModel;
		if (!parentComponent || !Array.isArray(parentComponent.Children)) return null;
		parentChildren = parentComponent.Children;
	}

	// Filter out the component to remove
	const newChildren = parentChildren.filter((c) => c.Id !== targetId);

	// If nothing was removed, return null
	if (newChildren.length === parentChildren.length) return null;

	return updateChildrenAtPath(view, parentPath, newChildren);
}

/** Get the parent path for a given component path (removes last element) */
export function getParentPath(path: ComponentPath): ComponentPath {
	if (path.length === 0) return [];
	return path.slice(0, -1);
}

/** Get component at path and determine if it's a container by looking up metadata */
export function getComponentAndCheckIfContainer(
	view: IContainerComponentModel,
	path: ComponentPath,
	componentByName: Map<string, any>,
): { component: IComponentModel | null; isContainer: boolean } {
	if (path.length === 0) {
		return { component: null, isContainer: false };
	}

	const component = getComponentAtPath(view.Children, path);
	if (!component) {
		return { component: null, isContainer: false };
	}

	const metadata = componentByName.get(component.Type);
	return {
		component,
		isContainer: metadata?.isContainer ?? false,
	};
}

/** Find the closest parent container path for adding children. Returns path to container and whether it's the view root. */
export function findClosestContainerPath(
	view: IContainerComponentModel,
	currentPath: ComponentPath,
	componentByName: Map<string, any>,
): { containerPath: ComponentPath; isRoot: boolean } {
	// Check if current component is a container
	const current = getComponentAndCheckIfContainer(view, currentPath, componentByName);
	if (current.isContainer) {
		return { containerPath: currentPath, isRoot: false };
	}

	// Walk up the path to find a parent container
	let checkPath = getParentPath(currentPath);
	while (checkPath.length > 0) {
		const parent = getComponentAndCheckIfContainer(view, checkPath, componentByName);
		if (parent.isContainer) {
			return { containerPath: checkPath, isRoot: false };
		}
		checkPath = getParentPath(checkPath);
	}

	// If no parent container found, add to root
	return { containerPath: [], isRoot: true };
}

/**
 * Transforms a View-relative component path to a SharedComponent-relative path.
 *
 * When a component is selected inside a ReferenceComponent, the path is View-relative:
 * [...referenceComponentPath, sharedComponentRootId, ...nestedPath]
 *
 * This function strips the ReferenceComponent prefix to get the SharedComponent-relative path:
 * [sharedComponentRootId, ...nestedPath]
 *
 * @param viewPath - The full View-relative path including the ReferenceComponent prefix
 * @param referenceComponentPath - The path to the ReferenceComponent in the View
 * @returns The SharedComponent-relative path
 */
export function transformViewPathToSharedComponentPath(viewPath: ComponentPath, referenceComponentPath: ComponentPath): ComponentPath {
	return viewPath.slice(referenceComponentPath.length);
}

export function getPathRelativeToSharedComponentRoot(path: ComponentPath, root: IContainerComponentModel): ComponentPath {
	return path[0] === root.Id ? path.slice(1) : path;
}

export type SharedComponentSelectionResolution = {
	sharedComponent: SharedComponentModel;
	root: IContainerComponentModel;
	pathInSharedComponent: ComponentPath;
	relativePath: ComponentPath;
};

export function resolveSharedComponentSelection(
	bundle: Bundle,
	selectedComponentPath: ComponentPath | null,
	selectedSharedComponentContext?: SharedComponentSelectionContext | null,
): SharedComponentSelectionResolution | null {
	if (!selectedComponentPath || !selectedSharedComponentContext) {
		return null;
	}

	const sharedComponent = bundle.SharedComponents.find((sc) => sc.Id === selectedSharedComponentContext.sharedComponentId);
	if (!sharedComponent) {
		return null;
	}

	const root = sharedComponent.Component as IContainerComponentModel;
	const pathInSharedComponent = transformViewPathToSharedComponentPath(selectedComponentPath, selectedSharedComponentContext.referenceComponentPath);
	const relativePath = getPathRelativeToSharedComponentRoot(pathInSharedComponent, root);

	return {
		sharedComponent,
		root,
		pathInSharedComponent,
		relativePath,
	};
}

/**
 * Resolves the selected component from the bundle, handling both View and SharedComponent contexts.
 *
 * When a component inside a SharedComponent (via ReferenceComponent) is selected, this function
 * resolves it from the SharedComponent's tree rather than the View's tree.
 *
 * @param bundle - The current bundle containing Views and SharedComponents
 * @param selectedItem - The currently selected item (View, SharedComponent, etc.)
 * @param selectedComponentPath - The path to the selected component
 * @param selectedSharedComponentContext - Context if the selection is inside a SharedComponent
 * @returns Object containing the resolved component and whether it's a SharedComponent root
 */
export function resolveSelectedComponent(
	bundle: Bundle,
	selectedItem: SelectedItem,
	selectedComponentPath: ComponentPath,
	selectedSharedComponentContext?: SharedComponentSelectionContext | null,
): { component: IComponentModel | null; isSharedComponentRoot: boolean } {
	let component: IComponentModel | null = null;
	let isSharedComponentRoot = false;

	if (selectedItem.type === ITEM_TYPE_VIEW) {
		const view = bundle.Views.find((v) => v.Id === selectedItem.id);
		if (!view) {
			return { component: null, isSharedComponentRoot: false };
		}

		// If the selection is inside a SharedComponent (via ReferenceComponent),
		// resolve it from the SharedComponent's tree, not the View's tree
		if (selectedSharedComponentContext) {
			const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
			if (!resolution) {
				console.warn('[resolveSelectedComponent] SharedComponent not found:', selectedSharedComponentContext.sharedComponentId);
				return { component: null, isSharedComponentRoot: false };
			}

			// If we're selecting the SharedComponent root itself
			if (resolution.pathInSharedComponent.length === 1 && resolution.pathInSharedComponent[0] === resolution.root.Id) {
				component = resolution.root;
				isSharedComponentRoot = true;
			} else {
				// Get nested component inside the SharedComponent
				component = getComponentAtPath(resolution.root.Children || [], resolution.relativePath);
			}
		} else {
			// Normal View component (not inside SharedComponent)
			component = getComponentAtPath(view.Children, selectedComponentPath);
		}
	} else if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
		const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
		if (!sharedComponent) {
			return { component: null, isSharedComponentRoot: false };
		}

		if (selectedComponentPath.length === 1 && selectedComponentPath[0] === sharedComponent.Component.Id) {
			component = sharedComponent.Component;
			isSharedComponentRoot = true;
		} else {
			const relativePath = getPathRelativeToSharedComponentRoot(selectedComponentPath, sharedComponent.Component as IContainerComponentModel);
			component = getComponentAtPath(sharedComponent.Component.Children || [], relativePath);
		}
	}

	return { component, isSharedComponentRoot };
}
